import React, { useState, useEffect, useCallback } from 'react';
import { itemUploadsApi, outletsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload, ArrowRight, CheckCircle, WarningCircle, XCircle,
  CaretDown, CaretUp, Package, CurrencyInr, Tag, Scales,
  ArrowClockwise, Trash, Eye, PencilSimple, FilePlus,
} from '@phosphor-icons/react';

/* ─── mapping section definitions ─── */
const SECTION_FIELDS = {
  identity: {
    label: 'Item Identity', icon: Package, required: true,
    fields: [
      { key: 'item_name', label: 'Item Name', required: true },
      { key: 'source_item_code', label: 'SKU / Item Code' },
    ],
  },
  quantity: {
    label: 'Quantity', icon: Tag, required: true,
    fields: [
      { key: 'quantity', label: 'Quantity', required: true },
    ],
  },
  financials: {
    label: 'Financials', icon: CurrencyInr, required: true,
    fields: [
      { key: 'gross_item_amount', label: 'Gross Amount', required: true },
      { key: 'discount_amount', label: 'Discounts', required: true },
      { key: 'net_item_amount', label: 'Net Amount', required: true },
      { key: 'tax_amount', label: 'Tax Amount', required: true },
    ],
  },
  revenue: {
    label: 'Revenue Bucket', icon: Scales, required: true,
    fields: [
      { key: 'revenue_bucket', label: 'Account Name', required: true },
    ],
  },
  classification: {
    label: 'Classification', icon: Tag, required: false, collapsed: true,
    fields: [
      { key: 'invoice_type', label: 'Invoice Type' },
      { key: 'category', label: 'Category' },
      { key: 'subcategory', label: 'Subcategory' },
      { key: 'item_group_name', label: 'Item Group Name' },
      { key: 'variant_label', label: 'Variant' },
      { key: 'measuring_unit', label: 'Measuring Unit' },
    ],
  },
};

const fmtCurrency = (v) =>
  v != null ? Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '-';

const badgeClass = {
  UPLOADED:  'bg-blue-500/20 text-blue-400',
  MAPPED:    'bg-violet-500/20 text-violet-400',
  VALIDATED: 'bg-amber-500/20 text-amber-400',
  CONFIRMED: 'bg-emerald-500/20 text-emerald-400',
  FAILED:    'bg-red-500/20 text-red-400',
};

/* ─── Column Select ─── */
const ColSelect = ({ value, onChange, headers, label, required }) => (
  <div className="flex items-center gap-2">
    <Select value={value || '__NONE__'} onValueChange={(v) => onChange(v === '__NONE__' ? '' : v)}>
      <SelectTrigger data-testid={`map-item-${label}`} className="bg-[#18181B] border-white/10 h-8 text-xs font-mono flex-1">
        <SelectValue placeholder="-- select --" />
      </SelectTrigger>
      <SelectContent className="max-h-56">
        <SelectItem value="__NONE__">-- not mapped --</SelectItem>
        {headers.map((h) => (
          <SelectItem key={h} value={h}>{h}</SelectItem>
        ))}
      </SelectContent>
    </Select>
    <span className={`text-xs min-w-[110px] truncate ${required ? 'text-zinc-200' : 'text-zinc-500'}`}>
      {label} {required && <span className="text-[#D4FF00]">*</span>}
    </span>
  </div>
);

/* ───────────────────────────────────────────────────────────────────────── */
const ItemUploadsPage = () => {
  const [uploads, setUploads] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState('');

  /* wizard */
  const [wizard, setWizard] = useState(null);
  const [mapping, setMapping] = useState({});
  const [metadataCols, setMetadataCols] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({ classification: true });

  /* validation / confirm */
  const [validating, setValidating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [valResult, setValResult] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);

  /* view data dialog */
  const [viewDataUpload, setViewDataUpload] = useState(null);
  const [viewDataRecords, setViewDataRecords] = useState([]);
  const [viewDataTotal, setViewDataTotal] = useState(0);
  const [viewDataLoading, setViewDataLoading] = useState(false);
  const [viewDataExpandedRow, setViewDataExpandedRow] = useState(null);

  /* reconciliation */
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  const [actionLoading, setActionLoading] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [uploadsRes, outletsRes] = await Promise.all([
        itemUploadsApi.list(),
        outletsApi.list(),
      ]);
      setUploads(uploadsRes.data);
      setOutlets(outletsRes.data);
    } catch (err) { toast.error('Failed to load uploads'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch reconciliation on mount
  useEffect(() => {
    const fetchRecon = async () => {
      setReconLoading(true);
      try {
        const res = await itemUploadsApi.reconciliation();
        setRecon(res.data);
      } catch (err) { toast.error('Failed to load reconciliation'); }
      finally { setReconLoading(false); }
    };
    fetchRecon();
  }, []);

  /* ─── upload ─── */
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedOutlet) { toast.error('Select an outlet before uploading'); return; }
    setUploading(true);
    try {
      const res = await itemUploadsApi.upload(file, selectedOutlet);
      const d = res.data;
      toast.success('File uploaded — mapping wizard opened');
      openWizardFromUpload(d);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const openWizardFromUpload = (d) => {
    const ad = d.auto_detected || {};
    setMapping(ad.suggested_mapping || {});
    setMetadataCols(ad.metadata_columns || {});
    setValResult(null);
    setExpandedRow(null);
    setCollapsedSections({ classification: true });
    setWizard({
      fileId: d.upload_id,
      fileName: d.file_name,
      headers: d.detected_headers || [],
      autoDetected: ad,
    });
  };

  /* ─── continue mapping ─── */
  const handleContinueMapping = async (upload) => {
    setActionLoading((s) => ({ ...s, [upload.id]: 'continue' }));
    try {
      const res = await itemUploadsApi.getDetail(upload.id);
      const u = res.data.upload;
      const m = res.data.mapping;
      const vs = u.validation_summary_json || {};
      const ad = vs.auto_detected || {};
      if (u.outlet_id) setSelectedOutlet(u.outlet_id);
      if (m) {
        setMapping(m.mapping_json || {});
        setMetadataCols(m.metadata_columns_json || {});
      } else {
        setMapping(ad.suggested_mapping || {});
        setMetadataCols(ad.metadata_columns || {});
      }
      setValResult(null);
      setExpandedRow(null);
      setCollapsedSections({ classification: true });
      setWizard({ fileId: u.id, fileName: u.file_name, headers: vs.headers || [], autoDetected: ad });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load session');
    } finally {
      setActionLoading((s) => ({ ...s, [upload.id]: null }));
    }
  };

  /* ─── validate ─── */
  const handleValidate = async () => {
    if (!wizard) return;
    setValidating(true);
    try {
      await itemUploadsApi.mapColumns(wizard.fileId, {
        mapping,
        metadata_columns: metadataCols,
        mapping_version: '1.0.0',
      });
      const res = await itemUploadsApi.validate(wizard.fileId);
      setValResult(res.data);
      toast.success('Validation complete');
      fetchData();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error('Session expired — please sign in again');
      } else if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        toast.error('Request timed out — the file may be too large. Try again.');
      } else {
        toast.error(detail || 'Validation failed');
      }
    } finally { setValidating(false); }
  };

  /* ─── confirm from wizard ─── */
  const handleConfirm = async () => {
    if (!wizard) return;
    setConfirming(true);
    try {
      const res = await itemUploadsApi.confirm(wizard.fileId);
      toast.success(`Confirmed — ${res.data.records_ingested} item records ingested`);
      setWizard(null);
      setValResult(null);
      fetchData();
      // Refresh reconciliation
      try { const r = await itemUploadsApi.reconciliation(); setRecon(r.data); } catch (reconErr) { /* recon refresh non-critical */ }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error('Session expired — please sign in again');
      } else if (status === 404) {
        toast.error('Upload session not found — it may have been deleted');
      } else if (status === 400) {
        toast.error(detail || 'Upload must be validated before confirmation');
      } else if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        toast.error('Request timed out — the file may be too large. Try again.');
      } else {
        toast.error(detail || 'Confirmation failed — check your connection and try again');
      }
    } finally { setConfirming(false); }
  };

  /* ─── table actions ─── */
  const handleConfirmFromTable = async (upload) => {
    setActionLoading((s) => ({ ...s, [upload.id]: 'confirm' }));
    try {
      const res = await itemUploadsApi.confirm(upload.id);
      toast.success(`Confirmed — ${res.data.records_ingested} records`);
      fetchData();
      try { const r = await itemUploadsApi.reconciliation(); setRecon(r.data); } catch (reconErr) { /* recon refresh non-critical */ }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 401) {
        toast.error('Session expired — please sign in again');
      } else if (status === 404) {
        toast.error('Upload session not found — it may have been deleted');
      } else if (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout')) {
        toast.error('Request timed out — the file may be too large. Try again.');
      } else {
        toast.error(detail || 'Confirmation failed');
      }
    }
    finally { setActionLoading((s) => ({ ...s, [upload.id]: null })); }
  };

  const handleRevalidate = async (upload) => {
    setActionLoading((s) => ({ ...s, [upload.id]: 'revalidate' }));
    try { await itemUploadsApi.revalidate(upload.id); toast.success('Re-validated'); fetchData(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    finally { setActionLoading((s) => ({ ...s, [upload.id]: null })); }
  };

  /* ─── Governed Delete ─── */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteStep, setDeleteStep] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDelete = async (upload) => {
    setDeleteTarget(upload);
    setDeleteStep('impact');
    setDeleteConfirmText('');
    setDeleteReason('');
    setDeleteLoading(true);
    try {
      const res = await itemUploadsApi.deleteImpact(upload.id);
      setDeleteImpact(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load delete impact');
      setDeleteTarget(null);
      setDeleteStep(null);
    } finally { setDeleteLoading(false); }
  };

  const executeDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const body = {};
      if (deleteImpact?.is_confirmed) {
        body.confirm_text = deleteConfirmText;
        body.reason = deleteReason;
      }
      await itemUploadsApi.delete(deleteTarget.id, body);
      toast.success(`Upload "${deleteTarget.file_name}" deleted`);
      setDeleteTarget(null);
      setDeleteStep(null);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally { setDeleteLoading(false); }
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteStep(null);
    setDeleteImpact(null);
  };

  const handleViewData = async (upload) => {
    setViewDataUpload(upload);
    setViewDataLoading(true);
    setViewDataExpandedRow(null);
    try {
      const res = await itemUploadsApi.canonicalPreview(upload.id, 50);
      setViewDataRecords(res.data.records || []);
      setViewDataTotal(res.data.total || 0);
    } catch (err) { setViewDataRecords([]); toast.error('Failed to load item records'); }
    finally { setViewDataLoading(false); }
  };

  const setField = (key, val) => setMapping((m) => ({ ...m, [key]: val }));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-zinc-400">Loading...</div></div>;

  return (
    <div className="space-y-6" data-testid="item-uploads-page">
      <div>
        <h1 className="text-2xl font-medium font-['Outfit'] tracking-tight">Sales by Items Upload</h1>
        <p className="text-sm text-zinc-400 mt-1">Canonical item-level sales truth — feeds Inventory, Accounts, and Reconciliation</p>
      </div>

      {/* upload card */}
      <div className="surface-base rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="space-y-2">
            <Label>Outlet <span className="text-[#D4FF00]">*</span></Label>
            <Select value={selectedOutlet} onValueChange={setSelectedOutlet}>
              <SelectTrigger data-testid="item-outlet-select" className="bg-[#18181B] border-white/10">
                <SelectValue placeholder="Select outlet" />
              </SelectTrigger>
              <SelectContent>
                {outlets.length === 0 && <SelectItem value="__none" disabled>No outlets</SelectItem>}
                {outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.display_name || o.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <p className="text-xs text-zinc-500">Upload a Sales by Items report (CSV / Excel). Auto-detects item name, financials, quantity, account name.</p>
          </div>
        </div>
        <div className="border-2 border-dashed border-[#D4FF00]/30 rounded-lg p-8 text-center bg-[#D4FF00]/5">
          <input type="file" accept=".csv,.xlsx,.xls" onChange={handleFileUpload} className="hidden" id="item-upload" data-testid="item-file-input" />
          <label htmlFor="item-upload" className="cursor-pointer">
            <Upload size={48} className="mx-auto text-[#D4FF00] mb-4" />
            <p className="text-sm text-zinc-300">{uploading ? 'Uploading...' : 'Click to upload Sales by Items report'}</p>
            <p className="text-xs text-zinc-500 mt-1">CSV, Excel — one row per item</p>
          </label>
        </div>
      </div>

      {/* ── RECONCILIATION CARD ── */}
      {recon && (recon.transaction_rows > 0 || recon.item_rows > 0) && (
        <div className="surface-base rounded-lg p-5" data-testid="reconciliation-card">
          <h3 className="text-sm font-medium text-zinc-300 mb-3 flex items-center gap-2">
            <Scales size={16} className="text-[#D4FF00]" /> Transaction vs Item Reconciliation
          </h3>
          <div className="grid grid-cols-3 gap-4 text-xs font-mono">
            <ReconRow label="Net Sales" txVal={recon.transaction_net} itemVal={recon.item_net} variance={recon.net_variance} />
            <ReconRow label="Tax" txVal={recon.transaction_tax} itemVal={recon.item_tax} variance={recon.tax_variance} />
            <ReconRow label="Final Total" txVal={recon.transaction_total} itemVal={recon.item_total} variance={recon.total_variance} />
          </div>
          <div className="flex gap-4 mt-2 text-[10px] text-zinc-500">
            <span>Transaction rows: {recon.transaction_rows}</span>
            <span>Item rows: {recon.item_rows}</span>
          </div>
        </div>
      )}

      {/* ── MAPPING WIZARD ── */}
      <Dialog open={!!wizard} onOpenChange={(open) => { if (!open) { setWizard(null); setValResult(null); } }}>
        <DialogContent className="bg-[#121214] border-white/10 max-w-5xl max-h-[92vh] overflow-y-auto p-0" aria-describedby="item-wizard-desc">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5 sticky top-0 bg-[#121214] z-10">
            <DialogTitle className="font-['Outfit'] text-lg tracking-tight">
              Item Mapping — <span className="font-mono text-sm text-[#D4FF00]">{wizard?.fileName}</span>
            </DialogTitle>
            <p id="item-wizard-desc" className="text-xs text-zinc-400 mt-1">Map source columns to canonical item fields</p>
          </DialogHeader>

          <div className="px-6 py-4 space-y-5">
            {Object.entries(SECTION_FIELDS).map(([sectionKey, sec]) => {
              const Icon = sec.icon;
              const isCollapsed = !!collapsedSections[sectionKey];
              const isCollapsible = !!sec.collapsed;
              return (
                <div key={sectionKey} className="rounded-md border border-white/5 overflow-hidden" data-testid={`item-section-${sectionKey}`}>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 px-4 py-2.5 bg-white/[0.02] hover:bg-white/[0.04] transition-colors text-left"
                    onClick={() => isCollapsible && setCollapsedSections((c) => ({ ...c, [sectionKey]: !c[sectionKey] }))}
                  >
                    <Icon size={16} className="text-[#D4FF00]" />
                    <span className="text-sm font-medium flex-1">{sec.label}</span>
                    {sec.required && <span className="text-[10px] uppercase tracking-widest text-[#D4FF00]/70 mr-2">required</span>}
                    {isCollapsible && (isCollapsed ? <CaretDown size={14} className="text-zinc-500" /> : <CaretUp size={14} className="text-zinc-500" />)}
                  </button>
                  {(!isCollapsible || !isCollapsed) && (
                    <div className="px-4 pb-4 pt-2 space-y-2">
                      {sec.fields.map((f) => (
                        <ColSelect key={f.key} value={mapping[f.key]} onChange={(v) => setField(f.key, v)} headers={wizard?.headers || []} label={f.label} required={f.required} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* ignored columns info */}
            {wizard?.autoDetected?.ignored_columns?.length > 0 && (
              <div className="rounded-md border border-white/5 p-3">
                <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Ignored Columns (POS-derived, not used)</p>
                <p className="text-xs text-zinc-600 font-mono">{wizard.autoDetected.ignored_columns.join(', ')}</p>
              </div>
            )}

            <Button onClick={handleValidate} disabled={validating} className="w-full bg-violet-500 hover:bg-violet-600" data-testid="item-validate-btn">
              {validating ? 'Validating...' : 'Save Mapping & Validate'}
            </Button>

            {valResult && <ItemValidationPanel valResult={valResult} expandedRow={expandedRow} setExpandedRow={setExpandedRow} onConfirm={handleConfirm} confirming={confirming} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── UPLOAD SESSIONS TABLE ── */}
      <div className="surface-base rounded-lg p-6">
        <h3 className="text-lg font-medium font-['Outfit'] mb-4">Item Upload Sessions</h3>
        {uploads.length === 0 ? (
          <div className="py-12 text-center text-zinc-500">No item uploads yet.</div>
        ) : (
          <div className="space-y-3" data-testid="item-uploads-list">
            {uploads.map((u) => (
              <ItemUploadRow
                key={u.id}
                upload={u}
                outlets={outlets}
                actionLoading={actionLoading[u.id]}
                onContinueMapping={() => handleContinueMapping(u)}
                onConfirm={() => handleConfirmFromTable(u)}
                onRevalidate={() => handleRevalidate(u)}
                onDelete={() => handleDelete(u)}
                onViewData={() => handleViewData(u)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── VIEW DATA DIALOG ── */}
      <Dialog open={!!viewDataUpload} onOpenChange={(open) => { if (!open) setViewDataUpload(null); }}>
        <DialogContent className="bg-[#121214] border-white/10 max-w-6xl max-h-[92vh] overflow-y-auto p-0" aria-describedby="item-view-desc">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/5 sticky top-0 bg-[#121214] z-10">
            <DialogTitle className="font-['Outfit'] text-lg tracking-tight">
              Confirmed Items — <span className="font-mono text-sm text-emerald-400">{viewDataUpload?.file_name}</span>
            </DialogTitle>
            <p id="item-view-desc" className="text-xs text-zinc-400 mt-1">{viewDataTotal} item records</p>
          </DialogHeader>
          <div className="px-6 py-4">
            {viewDataLoading ? <div className="py-12 text-center text-zinc-500">Loading...</div>
            : viewDataRecords.length === 0 ? <div className="py-12 text-center text-zinc-500">No records found.</div>
            : <ItemTable records={viewDataRecords} expandedRow={viewDataExpandedRow} setExpandedRow={setViewDataExpandedRow} />}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── GOVERNED DELETE DIALOG ── */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) closeDeleteModal(); }}>
        <DialogContent className="bg-[#121214] border-white/10 max-w-lg" aria-describedby="item-delete-desc">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-lg tracking-tight flex items-center gap-2">
              <Trash size={18} className="text-red-400" /> Delete Item Upload
            </DialogTitle>
          </DialogHeader>
          <div id="item-delete-desc">
            {deleteLoading && !deleteImpact ? (
              <div className="py-8 text-center text-zinc-500">Calculating impact...</div>
            ) : deleteStep === 'impact' && deleteImpact ? (
              <div className="space-y-4" data-testid="item-delete-impact-preview">
                <div className="rounded-lg bg-white/[0.02] border border-white/5 p-4 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">File</span>
                    <span className="font-mono text-zinc-200">{deleteImpact.file_name}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Type</span>
                    <span className="font-mono text-zinc-300">{deleteImpact.upload_type}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Status</span>
                    <span className={`font-mono ${deleteImpact.is_confirmed ? 'text-emerald-400' : 'text-zinc-400'}`}>{deleteImpact.status}</span>
                  </div>
                  {deleteImpact.outlet_name && (
                    <div className="flex justify-between text-xs">
                      <span className="text-zinc-500">Outlet</span>
                      <span className="font-mono text-zinc-300">{deleteImpact.outlet_name}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs">
                    <span className="text-zinc-500">Canonical Item Records</span>
                    <span className={`font-mono ${deleteImpact.canonical_record_count > 0 ? 'text-red-400' : 'text-zinc-400'}`}>{deleteImpact.canonical_record_count}</span>
                  </div>
                </div>

                {deleteImpact.is_confirmed && (
                  <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-3" data-testid="item-delete-warning">
                    <p className="text-xs text-red-300 leading-relaxed">
                      This will permanently remove the uploaded file and all <strong>{deleteImpact.canonical_record_count}</strong> canonical item records created from it. Explorer totals, item intelligence, and downstream analytics will change.
                    </p>
                    {deleteImpact.downstream_warning && (
                      <p className="text-[10px] text-red-400/60 mt-1.5">{deleteImpact.downstream_warning}</p>
                    )}
                  </div>
                )}

                {!deleteImpact.is_confirmed && (
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    This upload has not been confirmed. Only the upload session, mapping, and preview artifacts will be removed.
                  </p>
                )}

                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="ghost" size="sm" onClick={closeDeleteModal} className="text-xs text-zinc-400 hover:text-zinc-200" data-testid="item-delete-cancel">
                    Cancel
                  </Button>
                  {deleteImpact.is_confirmed ? (
                    <Button variant="destructive" size="sm" onClick={() => setDeleteStep('confirm')} className="text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20" data-testid="item-delete-proceed">
                      Proceed to Confirmation
                    </Button>
                  ) : (
                    <Button variant="destructive" size="sm" onClick={executeDelete} disabled={deleteLoading} className="text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20" data-testid="item-delete-execute-unconfirmed">
                      {deleteLoading ? 'Deleting...' : 'Delete Upload'}
                    </Button>
                  )}
                </div>
              </div>
            ) : deleteStep === 'confirm' && deleteImpact ? (
              <div className="space-y-4" data-testid="item-delete-confirm-step">
                <div className="rounded-lg bg-red-500/5 border border-red-500/20 p-3">
                  <p className="text-xs text-red-300 font-medium">Destructive Action</p>
                  <p className="text-[11px] text-red-300/80 mt-1 leading-relaxed">
                    You are about to permanently delete <strong>{deleteImpact.file_name}</strong> and {deleteImpact.canonical_record_count} canonical item records. This cannot be undone.
                  </p>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Type DELETE to confirm</label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="DELETE"
                    className="w-full bg-[#18181B] border border-white/10 rounded-md px-3 py-2 text-sm font-mono text-zinc-200 outline-none focus:border-red-500/40 placeholder:text-zinc-700"
                    data-testid="item-delete-confirm-input"
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5 block">Reason (optional)</label>
                  <input
                    type="text"
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Why are you deleting this upload?"
                    className="w-full bg-[#18181B] border border-white/10 rounded-md px-3 py-2 text-xs text-zinc-200 outline-none focus:border-white/20 placeholder:text-zinc-700"
                    data-testid="item-delete-reason-input"
                  />
                </div>

                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="ghost" size="sm" onClick={() => setDeleteStep('impact')} className="text-xs text-zinc-400 hover:text-zinc-200" data-testid="item-delete-back">
                    Back
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deleteConfirmText !== 'DELETE' || deleteLoading}
                    onClick={executeDelete}
                    className="text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 disabled:opacity-30"
                    data-testid="item-delete-execute-confirmed"
                  >
                    {deleteLoading ? 'Deleting...' : 'Permanently Delete'}
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ═══════ RECONCILIATION ROW ═══════ */
const ReconRow = ({ label, txVal, itemVal, variance }) => {
  const isGood = Math.abs(variance?.variance || 0) < 1;
  return (
    <div className="p-3 bg-zinc-800/30 rounded">
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</p>
      <div className="flex justify-between"><span className="text-zinc-500">Transactions</span><span className="text-zinc-300">{fmtCurrency(txVal)}</span></div>
      <div className="flex justify-between"><span className="text-zinc-500">Items</span><span className="text-zinc-300">{fmtCurrency(itemVal)}</span></div>
      <div className="flex justify-between mt-1 border-t border-white/5 pt-1">
        <span className="text-zinc-500">Variance</span>
        <span className={isGood ? 'text-emerald-400' : 'text-amber-400'}>
          {fmtCurrency(variance?.variance)} ({variance?.variance_percent}%)
        </span>
      </div>
    </div>
  );
};

/* ═══════ UPLOAD ROW ═══════ */
const ItemUploadRow = ({ upload: u, outlets, actionLoading, onContinueMapping, onConfirm, onRevalidate, onDelete, onViewData }) => {
  const vs = u.validation_summary_json || {};
  const fs = vs.financial_summary || {};
  const status = u.mapping_status;
  const outletObj = outlets.find((o) => o.id === u.outlet_id);
  const isLoading = !!actionLoading;

  return (
    <div className="rounded-lg border border-white/5 bg-white/[0.01] hover:bg-white/[0.02] transition-colors" data-testid={`item-upload-row-${u.id}`}>
      <div className="px-4 py-3 flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <p className="font-mono text-sm text-zinc-200 truncate" data-testid={`item-upload-filename-${u.id}`}>{u.file_name}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
            {outletObj && <span>{outletObj.display_name}</span>}
            <span>{new Date(u.uploaded_at).toLocaleDateString()}</span>
          </div>
        </div>
        <span className={`px-2.5 py-1 rounded text-xs font-mono font-medium ${badgeClass[status] || 'bg-zinc-800 text-zinc-400'}`} data-testid={`item-upload-status-${u.id}`}>
          {status}
        </span>
        {vs.rows_total && (
          <div className="flex items-center gap-4 text-xs">
            <div className="text-center"><p className="font-mono text-zinc-300">{vs.rows_total}</p><p className="text-zinc-600 text-[10px]">rows</p></div>
            {vs.unique_items && <div className="text-center"><p className="font-mono text-zinc-300">{vs.unique_items}</p><p className="text-zinc-600 text-[10px]">items</p></div>}
            {vs.avg_confidence != null && <div className="text-center"><p className="font-mono text-[#D4FF00]">{vs.avg_confidence}%</p><p className="text-zinc-600 text-[10px]">confidence</p></div>}
            {fs.net_amount != null && <div className="text-center"><p className="font-mono text-zinc-300">{fmtCurrency(fs.net_amount)}</p><p className="text-zinc-600 text-[10px]">net</p></div>}
          </div>
        )}
      </div>
      <div className="px-4 py-2 border-t border-white/5 flex items-center gap-2 flex-wrap">
        {(status === 'UPLOADED' || status === 'MAPPED') && (
          <>
            <ActionBtn icon={PencilSimple} label="Continue Mapping" onClick={onContinueMapping} loading={actionLoading === 'continue'} disabled={isLoading} testId={`item-continue-${u.id}`} />
            <ActionBtn icon={Trash} label="Delete" onClick={onDelete} loading={actionLoading === 'delete'} disabled={isLoading} variant="danger" testId={`item-delete-${u.id}`} />
          </>
        )}
        {status === 'VALIDATED' && (
          <>
            <ActionBtn icon={CheckCircle} label="Confirm" onClick={onConfirm} loading={actionLoading === 'confirm'} disabled={isLoading} variant="primary" testId={`item-confirm-${u.id}`} />
            <ActionBtn icon={ArrowClockwise} label="Re-validate" onClick={onRevalidate} loading={actionLoading === 'revalidate'} disabled={isLoading} testId={`item-reval-${u.id}`} />
            <ActionBtn icon={PencilSimple} label="Edit Mapping" onClick={onContinueMapping} loading={actionLoading === 'continue'} disabled={isLoading} testId={`item-edit-${u.id}`} />
            <ActionBtn icon={Trash} label="Delete" onClick={onDelete} loading={actionLoading === 'delete'} disabled={isLoading} variant="danger" testId={`item-delete-${u.id}`} />
          </>
        )}
        {status === 'CONFIRMED' && (
          <>
            <ActionBtn icon={Eye} label="View Data" onClick={onViewData} variant="primary" testId={`item-view-${u.id}`} />
            <ActionBtn icon={Trash} label="Delete" onClick={onDelete} loading={actionLoading === 'delete'} disabled={isLoading} variant="danger" testId={`item-delete-${u.id}`} />
          </>
        )}
        {status === 'FAILED' && (
          <>
            <ActionBtn icon={Eye} label="Inspect" onClick={onContinueMapping} loading={actionLoading === 'continue'} disabled={isLoading} testId={`item-inspect-${u.id}`} />
            <ActionBtn icon={Trash} label="Delete" onClick={onDelete} loading={actionLoading === 'delete'} disabled={isLoading} variant="danger" testId={`item-delete-${u.id}`} />
          </>
        )}
      </div>
    </div>
  );
};

/* ═══════ ACTION BUTTON ═══════ */
const ActionBtn = ({ icon: Icon, label, onClick, loading, disabled, variant, testId }) => {
  const base = 'flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors disabled:opacity-40';
  const styles = {
    default: 'bg-white/5 hover:bg-white/10 text-zinc-300',
    primary: 'bg-[#D4FF00]/10 hover:bg-[#D4FF00]/20 text-[#D4FF00]',
    danger: 'bg-red-500/10 hover:bg-red-500/20 text-red-400',
  };
  return (
    <button onClick={onClick} disabled={disabled || loading} className={`${base} ${styles[variant] || styles.default}`} data-testid={testId}>
      {loading ? <ArrowClockwise size={14} className="animate-spin" /> : <Icon size={14} />}
      {label}
    </button>
  );
};

/* ═══════ ITEM VALIDATION PANEL ═══════ */
const ItemValidationPanel = ({ valResult, expandedRow, setExpandedRow, onConfirm, confirming }) => (
  <div className="space-y-4 mt-2" data-testid="item-validation-result">
    <div className="grid grid-cols-6 gap-3">
      {[
        { label: 'Total', value: valResult.summary.rows_total, cls: '' },
        { label: 'Pass', value: valResult.summary.rows_pass, cls: 'text-emerald-400' },
        { label: 'Warn', value: valResult.summary.rows_warn, cls: 'text-amber-400' },
        { label: 'Fail', value: valResult.summary.rows_fail, cls: 'text-red-400' },
        { label: 'Items', value: valResult.summary.unique_items, cls: 'text-cyan-400' },
        { label: 'Confidence', value: `${valResult.summary.avg_confidence}%`, cls: 'text-[#D4FF00]' },
      ].map((s) => (
        <div key={s.label} className="text-center p-2.5 bg-zinc-800/50 rounded">
          <p className={`text-xl font-mono font-light ${s.cls}`}>{s.value}</p>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
        </div>
      ))}
    </div>
    <div className="grid grid-cols-3 gap-3">
      {[
        { label: 'Gross Amount', val: valResult.financial_summary.gross_amount },
        { label: 'Discounts', val: valResult.financial_summary.discounts },
        { label: 'Net Amount', val: valResult.financial_summary.net_amount },
        { label: 'Tax', val: valResult.financial_summary.tax_amount },
        { label: 'Final Amount', val: valResult.financial_summary.final_amount },
        { label: 'Quantity', val: valResult.financial_summary.total_quantity },
      ].map((s) => (
        <div key={s.label} className="p-2.5 bg-zinc-800/50 rounded">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
          <p className="text-base font-mono font-light">{fmtCurrency(s.val)}</p>
        </div>
      ))}
    </div>
    {/* bucket breakdown */}
    {valResult.bucket_totals && Object.keys(valResult.bucket_totals).length > 0 && (
      <div className="p-3 bg-zinc-800/30 rounded border border-white/5">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Revenue Buckets</p>
        <div className="grid grid-cols-3 gap-2 text-xs font-mono">
          {Object.entries(valResult.bucket_totals).map(([b, v]) => (
            <div key={b} className="flex justify-between"><span className="text-zinc-400">{b}</span><span className="text-zinc-200">{fmtCurrency(v)}</span></div>
          ))}
        </div>
      </div>
    )}
    {valResult.preview?.length > 0 && <ItemTable records={valResult.preview} expandedRow={expandedRow} setExpandedRow={setExpandedRow} />}
    <Button onClick={onConfirm} disabled={confirming} className="w-full bg-[#D4FF00] hover:bg-[#D4FF00]/80 text-black font-medium" data-testid="item-confirm-ingestion-btn">
      {confirming ? 'Ingesting...' : `Confirm Ingestion — ${valResult.summary.rows_total} items`}
    </Button>
  </div>
);

/* ═══════ ITEM TABLE ═══════ */
const ItemTable = ({ records, expandedRow, setExpandedRow }) => (
  <div className="overflow-x-auto rounded border border-white/5">
    <table className="w-full text-xs" data-testid="item-canonical-table">
      <thead>
        <tr className="bg-zinc-800/50 text-zinc-400 text-left">
          <th className="p-2 font-mono">Item Name</th>
          <th className="p-2 font-mono text-right">Qty</th>
          <th className="p-2 font-mono text-right">Gross</th>
          <th className="p-2 font-mono text-right">Disc</th>
          <th className="p-2 font-mono text-right">Net</th>
          <th className="p-2 font-mono text-right">Tax</th>
          <th className="p-2 font-mono text-right">Final</th>
          <th className="p-2 font-mono">Account</th>
          <th className="p-2 font-mono text-center">Status</th>
          <th className="p-2 font-mono text-right">Conf%</th>
        </tr>
      </thead>
      <tbody>
        {records.map((r, i) => (
          <React.Fragment key={r.id || i}>
            <tr className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors" onClick={() => setExpandedRow(expandedRow === i ? null : i)} data-testid={`item-row-${i}`}>
              <td className="p-2 font-mono text-zinc-300 max-w-[180px] truncate">{r.item_name_raw}</td>
              <td className="p-2 font-mono text-right">{r.quantity}</td>
              <td className="p-2 font-mono text-right">{fmtCurrency(r.gross_item_amount)}</td>
              <td className="p-2 font-mono text-right text-zinc-500">{fmtCurrency(r.discount_amount)}</td>
              <td className="p-2 font-mono text-right">{fmtCurrency(r.net_item_amount)}</td>
              <td className="p-2 font-mono text-right text-zinc-400">{fmtCurrency(r.tax_amount)}</td>
              <td className="p-2 font-mono text-right font-medium">{fmtCurrency(r.final_item_amount)}</td>
              <td className="p-2 font-mono text-zinc-400 max-w-[100px] truncate">{r.revenue_bucket || '-'}</td>
              <td className="p-2 text-center">
                {r.validation_status === 'PASS' && <CheckCircle size={16} className="text-emerald-400 inline" />}
                {r.validation_status === 'WARN' && <WarningCircle size={16} className="text-amber-400 inline" />}
                {r.validation_status === 'FAIL' && <XCircle size={16} className="text-red-400 inline" />}
              </td>
              <td className="p-2 font-mono text-right text-[#D4FF00]">{r.confidence_score}%</td>
            </tr>
            {expandedRow === i && (
              <tr><td colSpan={10} className="p-0"><ItemRowDetail record={r} /></td></tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
);

/* ─── Item row detail ─── */
const ItemRowDetail = ({ record: r }) => (
  <div className="bg-zinc-900/60 p-4 grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs border-t border-white/5">
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Identity</p>
      <div className="space-y-0.5 font-mono text-zinc-400">
        <div>Raw: <span className="text-zinc-300">{r.item_name_raw}</span></div>
        <div>Normalized: <span className="text-zinc-300">{r.item_name_normalized}</span></div>
        {r.source_item_code && <div>SKU: <span className="text-zinc-300">{r.source_item_code}</span></div>}
        {r.canonical_item_id && <div>Canonical ID: <span className="text-[#D4FF00]">{r.canonical_item_id}</span></div>}
      </div>
    </div>
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Classification</p>
      <div className="space-y-0.5 font-mono text-zinc-400">
        {r.category && <div>Category: <span className="text-zinc-300">{r.category}</span></div>}
        {r.subcategory && <div>Subcategory: <span className="text-zinc-300">{r.subcategory}</span></div>}
        {r.item_group_name && <div>Group: <span className="text-zinc-300">{r.item_group_name}</span></div>}
        {r.variant_label && <div>Variant: <span className="text-zinc-300">{r.variant_label}</span></div>}
        {r.measuring_unit && <div>Unit: <span className="text-zinc-300">{r.measuring_unit}</span></div>}
        {r.invoice_type && <div>Invoice: <span className="text-zinc-300">{r.invoice_type}</span></div>}
      </div>
    </div>
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Metadata</p>
      <div className="space-y-0.5 font-mono text-zinc-400">
        {r.source_metadata && Object.entries(r.source_metadata).map(([k, v]) => (
          <div key={k}>{k}: <span className="text-zinc-300">{v}</span></div>
        ))}
        {(!r.source_metadata || Object.keys(r.source_metadata).length === 0) && <p className="text-zinc-600 italic">None</p>}
      </div>
    </div>
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Validation</p>
      {r.validation_issues?.length > 0 ? r.validation_issues.map((iss) => (
        <p key={`${iss.rule}-${iss.message}`} className={`font-mono ${iss.severity === 'ERROR' ? 'text-red-400' : 'text-amber-400'}`}>[{iss.rule}] {iss.message}</p>
      )) : <p className="text-emerald-400 font-mono">All checks passed</p>}
    </div>
  </div>
);

export default ItemUploadsPage;
